// Market V2 adapter: strict regions, sorting, footnotes, detection

export const ALLOWED_REGIONS = ['North America', 'Europe', 'East Asia', 'South Asia', 'Southeast Asia', 'Oceania', 'LatAm', 'Middle East', 'Africa'];

function regionOrder(region) {
  const index = ALLOWED_REGIONS.indexOf(region);
  return index < 0 ? ALLOWED_REGIONS.length : index;
}

// Missing observations are never zero. Keep zero itself as a valid measurement.
export function marketNumber(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string' || !value.trim()) return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

export function formatMarketPercent(value) {
  const number = marketNumber(value);
  return number === null ? '—' : `${number}%`;
}

export function buildMarketProjection(size, rate, year = new Date().getFullYear()) {
  const initial = marketNumber(size);
  const cagr = marketNumber(rate);
  if (initial === null || initial <= 0 || cagr === null || cagr < -100) return [];
  const points = Array.from({ length: 7 }, (_, index) => ({
    year: year + index, marketSize: initial * Math.pow(1 + cagr / 100, index)
  }));
  return points.every((point) => Number.isFinite(point.marketSize)) ? points : [];
}

export function marketChartData(data) {
  if (!data?.MarketSizeEvidence) return buildMarketProjection(data?.CurrentMarketSize, data?.CAGR);
  const points = data.MarketSizeEvidence.points;
  if (!Array.isArray(points)) return [];
  return points.filter((point) => Number.isInteger(point?.year) && typeof point?.marketSize === 'number' && Number.isFinite(point.marketSize) && point.marketSize >= 0);
}

export function validateRegions(regionalDistribution) {
  const allowed = new Set(ALLOWED_REGIONS);
  const entries = Object.entries(regionalDistribution || {});
  const unknown = entries.map(([region]) => region).filter((r) => !allowed.has(r));
  return { isValid: unknown.length === 0, unknownRegions: unknown };
}

export function buildMarketDistribution(data, options = {}) {
  const { allowAnyRegions = false } = options;
  const { RegionalDistribution } = data || {};
  if (!allowAnyRegions) {
    const { isValid, unknownRegions } = validateRegions(RegionalDistribution);
    if (!isValid) return { unsupported: true, unknown: unknownRegions };
  }

  const list = Object.entries(RegionalDistribution || {})
    .map(([region, info]) => ({ region, percentage: marketNumber(info?.Share) }))
    .sort((a, b) => (b.percentage ?? -1) - (a.percentage ?? -1) || regionOrder(a.region) - regionOrder(b.region));

  const inside = list.filter((x) => x.percentage >= 4);
  const footnotes = list.filter((x) => x.percentage !== null && x.percentage < 4);

  return { inside, footnotes, all: list };
}

export function buildCagrData(data) {
  const rows = [];
  // Prepare references for weighted averages
  const growth = data?.RegionalGrowth || {};
  const distribution = data?.RegionalDistribution || {};
  const historicalPeriod = data?.RegionalPeriods?.historical || '2019-2024';
  const forecastPeriod = data?.RegionalPeriods?.forecast || data?.MarketSizeEvidence?.cagr_period || '2024-2030';
  const sourcedForecast = Boolean(data?.MarketSizeEvidence?.source_url);
  const rate = (row, field, periodField, period, legacyField, legacyPeriod) =>
    row?.[periodField] === period ? row?.[field] : (period === legacyPeriod ? row?.[legacyField] : null);

  const computeWeighted = (field) => {
    const entries = Object.entries(growth)
      .map(([region, g]) => {
        const share = marketNumber(distribution?.[region]?.Share);
        const value = marketNumber(g?.[field]);
        return { share, value };
      })
      .filter((e) => Number.isFinite(e.value) && e.share > 0);
    const totalShare = entries.reduce((acc, e) => acc + e.share, 0);
    if (!totalShare) return undefined;
    const weighted = entries.reduce((acc, e) => acc + e.value * e.share, 0) / totalShare;
    return Number.isFinite(weighted) ? Number(weighted.toFixed(1)) : undefined;
  };

  if (marketNumber(data?.CAGR) !== null) {
    const weightedBase = computeWeighted('CAGR_2024_2030_Base');
    const weightedDownside = computeWeighted('CAGR_2024_2030_Downside');
    const baseDesc = (data?.DriversList && data.DriversList[0]?.text) || 'Weighted by regional base-case growth';
    const downsideDesc = (data?.ChallengesList && data.ChallengesList[0]?.text) || 'Weighted by regional downside-case growth';

    rows.push({
      region: 'Global',
      marketSize: marketNumber(data?.RegionalDistributionGlobalSize ?? data?.CurrentMarketSize),
      cagr2019: sourcedForecast ? '—' : formatMarketPercent(data.CAGR),
      baseCase: sourcedForecast
        ? (data?.MarketSizeEvidence?.cagr_period === forecastPeriod ? formatMarketPercent(data.CAGR) : '—')
        : (Number.isFinite(weightedBase) ? `${weightedBase}%` : '—'),
      downsideCase: sourcedForecast ? '—' : (Number.isFinite(weightedDownside) ? `${weightedDownside}%` : '—'),
      baseCaseDesc: baseDesc,
      downsideDesc
    });
  }

  // Regional rows from payload

  const regionalRows = Object.entries(growth)
    .map(([region, g]) => ({
      region,
      share: marketNumber(distribution?.[region]?.Share),
      marketSize: marketNumber(distribution?.[region]?.MarketSize),
      cagr2019: formatMarketPercent(rate(g, 'HistoricalCAGR', 'HistoricalPeriod', historicalPeriod, 'CAGR_2019_2024', '2019-2024')),
      baseCase: formatMarketPercent(rate(g, 'ForecastCAGR', 'ForecastPeriod', forecastPeriod, 'CAGR_2024_2030_Base', '2024-2030')),
      downsideCase: formatMarketPercent(rate(g, 'DownsideCAGR', 'DownsidePeriod', forecastPeriod, 'CAGR_2024_2030_Downside', '2024-2030')),
      baseCaseDesc: g?.Drivers,
      downsideDesc: g?.Risks
    }))
    // Sort by share descending so the largest regions appear first, matching the left bar
    .sort((a, b) => (b.share ?? -1) - (a.share ?? -1) || regionOrder(a.region) - regionOrder(b.region))
    // Drop helper field after sorting without triggering unused var lint
    .map((row) => {
      const r = { ...row };
      delete r.share;
      return r;
    });

  return rows.concat(regionalRows);
}

export function formatCurrentMarketSizeToTrillions(value) {
  const num = marketNumber(value);
  if (num === null) return '';
  return `${(num / 1_000_000_000_000).toFixed(2)}T`;
}

export function isMarketV2Data(data) {
  try {
    if (data?.schemaVersion === 'market.v2' || data?.format === 'market_v2') return true;
    if (typeof data?.CurrentMarketSize !== 'number') return false;
    if (typeof data?.CAGR !== 'number') return false;

    const dist = data?.RegionalDistribution;
    const growth = data?.RegionalGrowth;

    const distOk =
      dist &&
      typeof dist === 'object' &&
      Object.values(dist).length >= 3 &&
      Object.values(dist).every((r) => typeof r?.Share === 'number' && typeof r?.MarketSize === 'number');

    const growthOk =
      growth &&
      typeof growth === 'object' &&
      Object.values(growth).length >= 3 &&
      Object.values(growth).every(
        (g) =>
          typeof g?.CAGR_2019_2024 === 'number' &&
          typeof g?.CAGR_2024_2030_Base === 'number' &&
          typeof g?.CAGR_2024_2030_Downside === 'number' &&
          typeof g?.Drivers === 'string' &&
          typeof g?.Risks === 'string'
      );

    if (!(distOk && growthOk)) return false;

    const { isValid } = validateRegions(dist);
    return isValid;
  } catch {
    return false;
  }
}
